import React from 'react';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { MoreVertical, ChevronRight, Check } from 'lucide-react';
import './menu.css';
import './ContextMenu.css';

/**
 * Baustein — Context-Menu.
 * Drei-Punkte-Trigger + Dropdown mit Items, Separator und Sub-Menüs.
 * Generisch — keine Domain-Logik (Decks/Karten/…) lebt hier.
 *
 * Komposition:
 *   <ContextMenu>
 *     <ContextMenu.Item icon={Copy} onSelect={...}>Kopieren</ContextMenu.Item>
 *     <ContextMenu.Separator />
 *     <ContextMenu.Submenu icon={Link} label="Verknüpfen">
 *       <ContextMenu.Item onSelect={...}>Deck A</ContextMenu.Item>
 *     </ContextMenu.Submenu>
 *     <ContextMenu.Separator />
 *     <ContextMenu.Item icon={Trash2} danger onSelect={...}>Löschen</ContextMenu.Item>
 *   </ContextMenu>
 *
 * Eigener Trigger via `trigger`-Prop (sonst MoreVertical-Button):
 *   <ContextMenu trigger={<button>Custom</button>}>…</ContextMenu>
 *
 * `onOpenChange(open: boolean)` — Radix-Durchreichung, wird bei Öffnen und Schließen gerufen.
 *
 * `onCloseAutoFocus(event)` — Radix-Durchreichung vom Content. Radix legt den Fokus beim
 * Schließen auf den Trigger zurück, und zwar in einem eigenen `setTimeout(0)` des
 * FocusScope-Cleanups: ein Konsument, der aus `onSelect` heraus selbst etwas fokussiert
 * (z.B. ein Feld, das der Menüeintrag gerade eingeblendet hat), verliert den Fokus wieder,
 * weil Radix' Timer zuletzt läuft. Wer das braucht, ruft hier `event.preventDefault()` und
 * fokussiert selbst. Ohne die Prop bleibt das Standardverhalten unverändert.
 *
 * `ContextMenu.Item` schließt das Menü per Default nach `onSelect`. Für Items, die man
 * mehrfach hintereinander bedienen will (z.B. eine Label-Auswahl), unterdrückt
 * `keepOpen` das automatische Schließen.
 *
 * `checked` (boolean) an `ContextMenu.Item` stellt eine Häkchen-Spalte VOR Icon und Text:
 * bei `true` mit Häkchen, bei `false` leer, damit markierte und unmarkierte Einträge
 * derselben Gruppe fluchten — deshalb bekommt jeder Eintrag der Gruppe die Prop, nicht nur
 * der markierte. Ohne die Prop gibt es keine Spalte. Die Spalte ist dasselbe Vokabular
 * (`ui-menu__check`) wie in `Select` und `FilterStrip.PillMenu`. Ein Eintrag mit `checked`
 * bleibt ein normales Item (Rolle `menuitem`, Schließverhalten wie oben, `keepOpen`
 * kombinierbar) — kein Radix-CheckboxItem, der Zustand wird vom Konsumenten geführt.
 */
export default function ContextMenu({ children, trigger, ariaLabel = 'Aktionen', onOpenChange, onCloseAutoFocus }) {
  // Dev-Absicherung: ein trigger, der Props/ref nicht an sein DOM-Element
  // weiterreicht, sieht normal aus, öffnet aber nichts (React verwirft
  // unbekannte Props lautlos). Die Probe erkennt beide Ausfälle: ref kommt
  // nicht an (node null) oder Radix' ARIA-Props fehlen am Element. Die Probe
  // klont dafür bei jedem Render eine neue Callback-Ref — ein eigener
  // Function-Ref des Konsumenten am Trigger sähe dadurch in Dev pro Commit
  // Detach/Attach-Zyklen; bekannte Eigenschaft, aktuell setzt kein Konsument
  // einen eigenen Ref auf den Trigger.
  const triggerProbe = React.useRef(null);
  // trigger ist bei jedem Render ein neues JSX-Element — der Effekt liefe sonst
  // bei jedem Commit erneut (z.B. bei häufig re-renderndem Host wie dem
  // Deck-Editor-Grid während Drag/Hover). hasWarnedRef wird erst gesetzt, wenn
  // tatsächlich gewarnt wurde: ein Wechsel von gesund zu kaputt innerhalb
  // desselben Mounts warnt weiterhin, nur eine zweite Warnung danach wird unterdrückt.
  const hasWarnedRef = React.useRef(false);
  const isDev = process.env.NODE_ENV !== 'production';
  let triggerElement = trigger;
  if (isDev && React.isValidElement(trigger)) {
    const existingRef = trigger.props.ref;
    triggerElement = React.cloneElement(trigger, {
      ref: (node) => {
        triggerProbe.current = node;
        if (typeof existingRef === 'function') existingRef(node);
        else if (existingRef && typeof existingRef === 'object') existingRef.current = node;
      },
    });
  }
  React.useEffect(() => {
    if (!isDev || !trigger || hasWarnedRef.current) return;
    const node = triggerProbe.current;
    if (!node || typeof node.hasAttribute !== 'function' || !node.hasAttribute('aria-haspopup')) {
      hasWarnedRef.current = true;
      console.warn(
        'ui/ContextMenu: Der übergebene trigger reicht Radix-Props nicht an sein DOM-Element weiter — ' +
        'der Knopf öffnet dann nichts. Props (inkl. Handler) und ref müssen am Button ankommen, ' +
        'siehe ActionBar.Action oder CardOverlays.Button.'
      );
    }
  }, [isDev, trigger]);

  return (
    <DropdownMenu.Root onOpenChange={onOpenChange}>
      <DropdownMenu.Trigger asChild>
        {triggerElement ?? (
          <button
            type="button"
            className="context-menu__trigger"
            aria-label={ariaLabel}
            onClick={(e) => e.stopPropagation()}
          >
            <MoreVertical size={16} />
          </button>
        )}
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          className="context-menu__content"
          sideOffset={4}
          align="end"
          collisionPadding={8}
          onCloseAutoFocus={onCloseAutoFocus}
        >
          {children}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}

/** Item — Icon + Label + onSelect. `danger`-Prop für destruktive Aktionen, `keepOpen` hält das Menü nach der Auswahl offen, `checked` stellt eine Häkchen-Spalte voran. */
ContextMenu.Item = function ContextMenuItem({ icon: Icon, children, onSelect, danger = false, disabled = false, keepOpen = false, checked }) {
  const cls = danger ? 'context-menu__item context-menu__item--danger' : 'context-menu__item';
  return (
    <DropdownMenu.Item
      className={cls}
      disabled={disabled}
      // keepOpen unterdrückt Radix' automatisches Schließen — für Einträge,
      // die man mehrfach hintereinander bedient (Label-Sektion des Deck-Editors).
      onSelect={(e) => { if (keepOpen) e.preventDefault(); onSelect?.(); }}
    >
      {checked !== undefined && (
        <span className="ui-menu__check">
          {checked ? <Check size={14} aria-hidden /> : null}
        </span>
      )}
      {Icon && <Icon size={14} aria-hidden />}
      <span>{children}</span>
    </DropdownMenu.Item>
  );
};

/** Visueller Trenner zwischen Item-Gruppen. */
ContextMenu.Separator = function ContextMenuSeparator() {
  return <DropdownMenu.Separator className="context-menu__separator" />;
};

/**
 * Unter dieser Fensterbreite hat ein seitlich ausklappendes Untermenü keinen Platz
 * neben seinem Elternmenü: beide tragen mindestens 180px, ein langes Label macht das
 * Elternmenü breiter, und ein rechts oben verankertes Menü lässt links erst gar nichts
 * übrig. Radix kann das nicht auffangen — es kippt das Untermenü auf die andere Seite
 * (die genauso eng ist) und seine `shift`-Middleware läuft mit
 * `mainAxis: true, crossAxis: false`, verschiebt bei seitlicher Platzierung also nur
 * senkrecht; `side`/`align` am `SubContent` sind wirkungslos, weil Radix sie nach dem
 * Prop-Spread fest überschreibt. Darunter klappt das Untermenü deshalb nicht aus,
 * sondern steht als Abschnitt IM Elternmenü — dort hält Radix es ohnehin im Viewport
 * und ein Untermenü seitlich zu treffen ist am Finger sowieso mühsam.
 */
const SUBMENU_INLINE_QUERY = '(max-width: 520px)';

function useSubmenuInline() {
  const [inline, setInline] = React.useState(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
    return window.matchMedia(SUBMENU_INLINE_QUERY).matches;
  });

  React.useEffect(() => {
    if (typeof window.matchMedia !== 'function') return undefined;
    const mediaQueryList = window.matchMedia(SUBMENU_INLINE_QUERY);
    const handleChange = (event) => setInline(event.matches);
    mediaQueryList.addEventListener('change', handleChange);
    return () => mediaQueryList.removeEventListener('change', handleChange);
  }, []);

  return inline;
}

/**
 * Sub-Menü mit eigenem Trigger und Content. Children sind Items des Sub-Menüs.
 * Auf schmalen Fenstern (siehe `SUBMENU_INLINE_QUERY`) klappt nichts aus: die Einträge
 * stehen mit einer Abschnitts-Überschrift direkt im Elternmenü.
 */
ContextMenu.Submenu = function ContextMenuSubmenu({ icon: Icon, label, children }) {
  const inline = useSubmenuInline();

  if (inline) {
    return (
      <DropdownMenu.Group className="context-menu__group">
        <DropdownMenu.Label className="context-menu__group-label">
          {Icon && <Icon size={13} aria-hidden />}
          <span>{label}</span>
        </DropdownMenu.Label>
        {children}
      </DropdownMenu.Group>
    );
  }

  return (
    <DropdownMenu.Sub>
      <DropdownMenu.SubTrigger className="context-menu__item context-menu__subtrigger">
        {Icon && <Icon size={14} aria-hidden />}
        <span>{label}</span>
        <ChevronRight size={14} className="context-menu__chevron" aria-hidden />
      </DropdownMenu.SubTrigger>
      <DropdownMenu.Portal>
        <DropdownMenu.SubContent className="context-menu__content" sideOffset={4} collisionPadding={8}>
          {children}
        </DropdownMenu.SubContent>
      </DropdownMenu.Portal>
    </DropdownMenu.Sub>
  );
};
